'use client'

import { clsx } from 'clsx'
import {
  type Dispatch,
  type FC,
  type RefObject,
  type SetStateAction,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from 'react'
import { FormattedMessage } from 'react-intl'
import { useWindowSize } from 'usehooks-ts'

import { IconMinus, IconPlus } from '@redteclab/icons'

import { Button, Chip } from 'base-ui'

import { useStoryblokStory } from '../../../../context/StoryblokStoryContext'

import { type CategoryHierarchyResponseDto } from './CategoryHierarchyResponseDto.type'

import styles from './StoryblokCategoryChipNavigation.module.css'

const getComputedStyleInPixels = (value: string): number =>
  Number.parseInt(value, 10)

/**
 * Calculates the width of the list of chips.
 *
 * ```
 * | Container                         |
 * | List                | | +showMore |
 * | Chip Chip Chip Chip |
 * | Chip Chip Chip Chip |
 * | Chip -showLess      |
 * ```
 */
const getListWidth = ({
  chips,
  containerEl,
  listEl,
  showMoreEl,
}: {
  chips: HTMLElement[]
  containerEl: HTMLDivElement
  listEl: HTMLDivElement
  showMoreEl: HTMLButtonElement
}): { counter: number; listWidth: number } => {
  const containerComputedStyle = globalThis.getComputedStyle(containerEl)

  const maxWidth =
    containerEl.getBoundingClientRect().width -
    getComputedStyleInPixels(containerComputedStyle.columnGap) -
    showMoreEl.getBoundingClientRect().width

  const listColumnGap = getComputedStyleInPixels(
    globalThis.getComputedStyle(listEl).columnGap,
  )

  let listWidth = -listColumnGap

  let counter = 0

  for (const chip of chips) {
    const chipSpace = listColumnGap + chip.getBoundingClientRect().width

    if (listWidth + chipSpace < maxWidth) {
      listWidth += chipSpace
      counter += 1
    } else {
      break
    }
  }

  return {
    counter,
    listWidth: Math.ceil(listWidth),
  }
}

const useChipNavigation = (): {
  containerRef: RefObject<HTMLDivElement | null>
  expanded: boolean
  listRef: RefObject<HTMLDivElement | null>
  setExpanded: Dispatch<SetStateAction<boolean>>
  showMoreRef: RefObject<HTMLButtonElement | null>
  showMoreVisible: boolean
} => {
  const [expanded, setExpanded] = useState(false)
  const [showMoreVisible, setShowMoreVisible] = useState(false)
  const { width } = useDeferredValue(
    useWindowSize({
      initializeWithValue: false,
    }),
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const showMoreRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const containerEl = containerRef.current
    const listEl = listRef.current
    const showMoreEl = showMoreRef.current

    if (
      width === undefined ||
      containerEl === null ||
      listEl === null ||
      showMoreEl === null
    ) {
      return
    }

    if (expanded) {
      listEl.style.width = ''

      return
    }

    const chips = [...listEl.children].filter(
      (item): item is HTMLElement =>
        item instanceof HTMLElement && Boolean(item.dataset.chip),
    )

    const { counter, listWidth } = getListWidth({
      chips,
      containerEl,
      listEl,
      showMoreEl,
    })

    if (counter === chips.length) {
      listEl.style.width = ''
      setShowMoreVisible(false)
    } else {
      listEl.style.width = `${listWidth}px`
      setShowMoreVisible(true)
    }
  }, [expanded, width])

  return {
    containerRef,
    expanded,
    listRef,
    setExpanded,
    showMoreRef,
    showMoreVisible,
  }
}

type StoryblokCategoryChipNavigationProps = {
  categories?: CategoryHierarchyResponseDto[]
}

export const StoryblokCategoryChipNavigation: FC<
  StoryblokCategoryChipNavigationProps
> = ({ categories }) => {
  const {
    containerRef,
    expanded,
    listRef,
    setExpanded,
    showMoreRef,
    showMoreVisible,
  } = useChipNavigation()

  const story = useStoryblokStory()

  if (story.content.component !== 'AkeneoCategoryPage' || !categories) {
    return null
  }

  return (
    <nav className="container hidden tablet:block">
      <div className="flex gap-4" ref={containerRef}>
        <div
          className={clsx(
            'flex flex-wrap gap-4',
            !expanded &&
              styles['storyblok-category-chip-navigation__list_expanded'],
          )}
          ref={listRef}
        >
          <Chip data-chip selected size="large">
            {story.content.title}
          </Chip>
          {categories
            // we display current category first, so we filter it out here
            .filter(
              (category) =>
                category.akeneoCode !== story.content.akeneoCategoryCode,
            )
            .map((category) => (
              <Chip
                data-chip
                href={category.seoLink}
                key={category.akeneoCode}
                size="large"
              >
                {category.name}
              </Chip>
            ))}
          <Button
            className={clsx(!expanded && 'hidden')}
            onClick={() => {
              setExpanded(false)
            }}
            size="large"
            startIcon={<IconMinus />}
            variant="text"
          >
            <FormattedMessage id="categoryPage.navigation.showLessCategories" />
          </Button>
        </div>
        <Button
          className={clsx(
            expanded && 'hidden',
            !expanded && !showMoreVisible && 'invisible',
          )}
          onClick={() => {
            setExpanded(true)
          }}
          ref={showMoreRef}
          size="large"
          startIcon={<IconPlus />}
          variant="text"
        >
          <FormattedMessage id="categoryPage.navigation.showMoreCategories" />
        </Button>
      </div>
    </nav>
  )
}
